/**
 * @typedef {GeolocationPosition} GeoLocationPositionExtended
 * @property {number} attempt
 * @property {number} watchId
 */

import { createPathWithAllFilters } from "@modules/azm/api"
import { getCategories, getCategoriesAsString } from "@modules/azm/category.ts"
import { getShowResultsButtonLocationModal } from "@modules/azm/dom"
import { hideCityAddressesMobile, showCityAddressesMobile } from "@modules/azm/filter-selection/city-addresses-select"
import { fetchApiWithNewLocation } from "@modules/azm/filter-selection/location-input"
import { setShowResultsButton, setShowResultsButtonLoading } from "@modules/azm/filter-selection/show-results-button"
import { getFilters, getFiltersAsString } from "@modules/azm/filter.ts"
import { InfoPanel } from "@modules/info-panel/InfoPanel"
import { getQueryParametersAsString, removeQueryParameter, setQueryParameter } from "@modules/util/query-parameter"
import { AutocompleteLds } from "../autocomplete/AutocompleteLds"
import { LadiesMain } from "../global/ladies-main"
import { isMobile } from "../util/device"
import { openGenericModal } from "../util/generic-modal"
import { BreakpointName, getCurrentBreakpoint } from "../util/responsive"
import { createNewPathForLocationSlug } from "../util/url"
import { getCurrentPositionUntil } from "./GeoLocation"
import { PositionError } from "./PositionError"
import { setFilterLocation } from "./location"

export class LocationSelectorClass {
    regionCallback: typeof this.toggleLocationSelectorContainer
    locationCallback: typeof this.requestGeoLocationInfo
    toggleOverlayCallback: typeof this.toggleLocationSuggestionOverlay
    emptySearchInput: typeof $
    toggleElements: typeof $
    regioSearchInput: typeof $
    autoDenyTimer = 0
    autoDenied = false
    explicitlyDenied = false
    requestUri = "/api/nearest-city/"
    retryHash = "#geoRetry"
    animationDuration = "400"
    regioSlideTimer = 0
    inputSelected = false
    actualLocationName = ""
    selectedLocation = ""
    locationButton: typeof $
    lastWasAutoFocus = false
    autocomplete: AutocompleteLds
    originalAddresses = ""

    constructor() {
        this.originalAddresses = document.querySelector<HTMLSelectElement>('select[name="address"]')?.innerHTML || ""
        // Event Listeners
        $(window).on("open.regio-selector", this.togglePageContainer.bind(this))

        // Autocomplete
        this.autocomplete = new AutocompleteLds({
            elementsInput: "#regiosuche",
            elementsForm: ".regio-form",
        })
        this.autocomplete.init()

        this.regioSearchInput = $("#regiosuche")
        this.locationButton = $("#button-search")
        this.emptySearchInput = $("#button-empty-location")
        this.actualLocationName = this.regioSearchInput.val()

        if ("index" === SiteInfo.controller) {
            this.locationButton.on("click", this.openLocationButtonLink.bind(this))
            this.regioSearchInput
                .on("input change focusout paste", this.disableLocationButton.bind(this))
                .on("change.value:Autocomplete", this.redirectOnValidAutocomplete.bind(this))
                .on("close.value:Autocomplete", this.changeSearchTextToActiveOption.bind(this))
        }

        if ("" !== this.actualLocationName) {
            this.disableLocationButton()
        }

        const modal = $("#fltRegio")
        const coordinatesButton = $("#button-get-coordinates")
        const reloadButtonSelector = "#button-reload-site"
        const toggleElements = ["#regioauswahl", "#regioclose"]

        if (SiteInfo.isIndex) {
            toggleElements.unshift("#regiosuche")
            //toggleElements.unshift('#button-get-coordinates');
        }

        this.toggleElements = $(toggleElements.join(","))

        // Erstelle Callback mit Korrekt gesetztem Kontext zu `this`
        this.regionCallback = this.toggleLocationSelectorContainer.bind(this)
        this.locationCallback = this.requestGeoLocationInfo.bind(this)

        modal.on("hide.bs.modal", () => {
            // reset location modal
            this.autocomplete.cancelRequest()
            this.regioSearchInput.val(
                SearchConfig && "options" in SearchConfig && SearchConfig.options.locationType !== "overall"
                    ? SearchConfig.location.name || ""
                    : "",
            )
            setShowResultsButton(SearchConfig.advertCount)
            this.autocomplete.selectedLocation = undefined
            this.selectedLocation = ""
            if (isMobile()) {
                const select = document.querySelector<HTMLSelectElement>('select[name="address"]')
                if (!this.originalAddresses) {
                    hideCityAddressesMobile()
                } else if (select) {
                    showCityAddressesMobile()
                    select.innerHTML = this.originalAddresses
                }
            }
        })

        this.toggleElements.click(this.regionCallback)
        coordinatesButton.click(this.locationCallback)
        $(document).on("click", reloadButtonSelector, this.reloadPage.bind(this, false))

        // TODO why, never used in stbail
        // if (siteConfig.redirectToGeoLocation) {
        //     LadiesMain.changeUrl(history.state || {}, "", location.href.replace("standort/", ""))
        //     this.locationCallback()
        // }

        this.emptySearchInput.click(() => {
            this.regioSearchInput.val("")
            this.regioSearchInput.focus()

            setTimeout(() => {
                this.enableLocationButton()

                if (isMobile()) {
                    this.regioSearchInput.data("regioSlug", this.regioSearchInput.val())

                    $(document).trigger("updated.regio:Autocomplete", [
                        { plz: "", slug: "", type: "overall", cleared: true },
                    ])
                    return
                }

                this.autocomplete.showAlleRegionenList = true

                this.regioSearchInput.autocomplete("search", "alle regionen")

                this.autocomplete.showAlleRegionenList = false
            }, 50)
        })

        this.toggleOverlayCallback = this.toggleLocationSuggestionOverlay.bind(this)
        this.regioSearchInput.on("focus", this.toggleOverlayCallback)

        $("#button-my-location").click(() => {
            $("#button-get-coordinates").click()
        })

        if (this.retryHash === location.hash) {
            this.regionCallback(new MouseEvent("click"))
            coordinatesButton.click()
        }

        if (!this.regioSearchInput.val() || "index" !== SiteInfo.controller) {
            return
        }

        this.regioSearchInput.select()
        this.regioSearchInput.autocomplete("search", this.regioSearchInput.val())
    }

    disableLocationButton() {
        if ("" === this.regioSearchInput.val() || "" !== this.selectedLocation) {
            return void this.enableLocationButton()
        }

        this.locationButton.attr("disabled", true)
    }

    enableLocationButton() {
        this.locationButton.prop("disabled", false)
    }

    redirectOnValidAutocomplete(_: MouseEvent, eventData: any) {
        if (!eventData) {
            return
        }

        const item = eventData.item || {}
        const autocomplete = eventData.instance
        let locationType = "ort"
        const plz = item.Plz ? `${item.Plz}-` : ""
        let pageSlug = `/${SiteInfo.redirectToPageSlug}`
        const keepEnabled = eventData && "change" === eventData.event.type && this.lastWasAutoFocus

        if (
            !keepEnabled &&
            "" !== eventData.value &&
            autocomplete.actualInputValue === eventData.value &&
            !eventData.autoFocus
        ) {
            this.lastWasAutoFocus = false
            this.selectedLocation = ""

            return void this.disableLocationButton()
        }

        this.lastWasAutoFocus = eventData.autoFocus

        if (!eventData.autoFocus && "" !== eventData.value && item.value !== eventData.value) {
            return
        }

        if ("" === eventData.value) {
            // Bei leerer Location wird kein Page-Slug benötigt, da hier
            // 	stattdessen das data-href des Buttons verwendet wird
            this.selectedLocation = ""

            return void this.enableLocationButton()
        }

        if ("" !== item.GebietsID) {
            locationType = "region"
        }

        if (item.slug in SiteInfo.redirectToPageSlugSpecial) {
            pageSlug = `/${SiteInfo.redirectToPageSlugSpecial[item.slug]}`
        }

        if (locationType in SiteInfo.redirectToPageSlugSpecial) {
            pageSlug = `/${SiteInfo.redirectToPageSlugSpecial[locationType]}`
        }

        // Da hier Ortsslugs existieren und diesen Slugs kein pageSlug für
        // 	die Home-Seite notwendig ist, diesen hier entfernen
        if ("/home" === pageSlug) {
            pageSlug = ""
        }

        this.selectedLocation = plz + item.slug + pageSlug
        this.enableLocationButton()
    }

    changeSearchTextToActiveOption(_: Event, eventData: any) {
        if ("" === this.selectedLocation || !eventData.item) {
            return
        }

        this.regioSearchInput.val(eventData.item.label)
    }

    openLocationButtonLink(clickEvent: MouseEvent) {
        const buttonElement = $(clickEvent.currentTarget)

        clickEvent.stopImmediatePropagation()
        clickEvent.preventDefault()

        if (buttonElement.is("[disabled]")) {
            return
        }

        location.href =
            "" !== this.selectedLocation
                ? window.location.origin + "/" + this.selectedLocation
                : window.location.origin + buttonElement.data("href")
    }

    sourceData(request: any, response: any) {
        $.ajax({
            url: "/api/location/",
            data: { searchString: request.term },
            dataType: "json",
            xhrFields: {
                withCredentials: true,
            },
            // success: response,
            error: () => {
                response([])
            },
            success: (data: any) => {
                if (data.length === 0) {
                    $("#regiosuche").removeClass("autocomplete-open")
                }

                siteConfig.locationSuggestions = data
                // wenn nach PLZ gesucht wurde, die Source ist ein Objekt
                if (siteConfig.locationSuggestions.constructor !== Array) {
                    const newSuggestionsArray: string[] = []
                    $.each(siteConfig.locationSuggestions, (_: number, option: any) => {
                        newSuggestionsArray.push(option)
                    })
                    siteConfig.locationSuggestions = newSuggestionsArray
                }

                $(".regio-form").removeClass("submit-disabled")

                return response(siteConfig.locationSuggestions)
            },
        })
    }

    sourceDataSuggested = (_: any, response: any) => response(siteConfig.locationSuggestions)

    onSelect = (_: Event, ui: any) => {
        const fieldItem = ui.item
        const locationForm = $(".regio-form")

        this.regioSearchInput
            .val(fieldItem.value)
            .data("regioSelected", fieldItem.value || "")
            .data("regioSlug", fieldItem.slug.replace(" ", "-").toLowerCase() || "")
            .data("regioPostal", fieldItem.plz || "")
            .data("regioLand", fieldItem.land || "")

        locationForm.removeClass("submit-disabled")

        $(".ui-autocomplete").hide()
        $("#regiosuche").removeClass("autocomplete-open")

        locationForm.submit()
    }

    toggleLocationSuggestionOverlay(toggleEvent: Event) {
        const vorschlagCollapse = $("#ort-vorschlag")
        const doc = $(document)

        switch (toggleEvent.type) {
            case "focus":
                if (vorschlagCollapse.is(".collapse.in")) {
                    return
                }

                doc.on("click", this.toggleOverlayCallback)
                vorschlagCollapse.collapse("show")
                break

            default: {
                const clickElement = $(toggleEvent.target)

                if (clickElement.closest("#page-regio-container").length > 0) {
                    return
                }

                doc.off("click", this.toggleOverlayCallback)

                // Der User hat die Regiosuche zugeklappt
                if ("undefined" !== typeof SearchConfig && SearchConfig.filteredSearchText != null) {
                    SearchConfig.filteredSearchText = null

                    // Generiere die Regio-Urls wieder MIT Orte
                    // TODO fix i guess
                    // Ladies.Anzeigenmarkt.controller.removeOptionsFromUrl(SearchConfig.options.suchTextOhneOrte)
                }

                // Der User hat eine Location ausgewählt
                if (clickElement.is(".ui-corner-all")) {
                    this.regioSearchInput?.blur()
                }
                vorschlagCollapse.collapse("hide")
                break
            }
        }
    }

    toggleLocationSelectorContainer(clickEvent: MouseEvent) {
        if (SiteInfo.isIndex) {
            // Animation für die Index-Seite
            this.toggleIndexContainer.call(this, clickEvent)
        } else {
            // Animation für alle weiteren Seiten
            this.togglePageContainer.call(this)
        }
    }

    toggleIndexContainer(clickEvent: MouseEvent) {
        if (!isMobile()) {
            const caller = $(clickEvent.currentTarget)
            const clickElement = $(clickEvent.target)
            const animationContainer = $("#regio-vorschlaege")
            const autoCompleteElement = $(".ui-autocomplete")
            let keepOpen = false

            // Bei klick auf das öffnende Element das Durchlaufen bis zum
            // 	document unterbinden, um doppeltes triggern zu untersagen
            this.stopEventBubbling(clickEvent)

            if (this.toggleElements.is(caller)) {
                // Animiertes öffnen des Vorschlages-Containers
                this.containerAnimation(animationContainer)

                // Trigger auf document setzen
                this.clearToggleHandlers.call(this)
                $(document).on("click", this.regionCallback)

                caller.select()
            } else {
                // Den Vorschlags-Container in folgenden Fällen offen halten:
                keepOpen =
                    // Das geklickte Element ist das öffnende Element
                    this.toggleElements.is(clickElement) ||
                    // Das geklickte Element ist der Vorschlags-Container
                    animationContainer.is(clickElement) ||
                    // Das geklickte Element ist ein Kind-Element des Containers
                    animationContainer.has(clickElement).length > 0 ||
                    // Das angeklickte Element ist der "Aktuelle Standort" Button
                    $("#button-get-coordinates").is(clickElement) ||
                    // Das geklickte Element ist Teil der Autovervollständigung
                    autoCompleteElement.is(clickElement) ||
                    autoCompleteElement.has(clickElement).length > 0 ||
                    // Es wird über eine Klasse vorgeschrieben, dass das Element
                    //	offen bleiben soll
                    animationContainer.hasClass("keep-open")

                if (keepOpen) {
                    return
                }

                // Animiertes schließen des Vorschlages-Containers
                this.containerAnimation(animationContainer, "hide", "400")

                // Trigger wieder auf das öffnende Element setzen
                this.clearToggleHandlers.call(this)
                this.toggleElements.on("click", this.regionCallback)
            }
        }
    }

    containerAnimation = (element: typeof $, newType = "show", newSpeed = "400") => {
        if (!element) {
            return
        }

        const type = newType || "show"
        const speed = newSpeed || "400"
        if ([BreakpointName.Large, BreakpointName.Medium].includes(getCurrentBreakpoint())) {
            element.stop().animate(
                {
                    width: type,
                    //opacity: type
                },
                speed,
                "swing",
            )
        } else {
            element.stop().animate(
                {
                    height: type,
                    //opacity: type
                },
                speed,
                "swing",
            )

            const indexBanner = $(".index-banner")
            if (indexBanner.length > 0) {
                if (type === "show") {
                    indexBanner.stop().animate(
                        {
                            opacity: 0.05,
                        },
                        speed,
                    )
                } else {
                    indexBanner.stop().animate(
                        {
                            opacity: 1,
                        },
                        speed,
                    )
                }
            }
        }
    }

    togglePageContainer() {
        const regioContainer = $("#page-regio-container")
        const regioMenu = $("#page-regio-toggle")

        if (!regioMenu.hasClass("active")) {
            regioMenu.addClass("active")
            regioMenu.find("> .btn > span").addClass("flaticon-collapse3").removeClass("flaticon-expand22")

            regioContainer.stop().slideDown(this.animationDuration, null, () => {
                this.regioSearchInput.focus()
                this.regioSearchInput.select()
            })

            $("html, body").animate({ scrollTop: regioContainer.offset().top }, this.animationDuration)
        } else {
            regioMenu.removeClass("active")
            regioMenu.find("> .btn > span").addClass("flaticon-expand22").removeClass("flaticon-collapse3")

            regioContainer.stop().slideUp(this.animationDuration)

            $("html, body").animate({ scrollTop: 0 }, this.animationDuration)
        }
    }

    requestGeoLocationInfo(event: (...args: any) => void, newFilterUrl = "") {
        const filterUrl = newFilterUrl || ""

        this.autoDenyTimer = new Date().getTime()

        const success = this.requestGeoLocationSuccess.bind(this, filterUrl)
        const failure =
            typeof event === "function"
                ? event.bind(null, this.requestGeoLocationError.bind(this, true), this.autoDenyTimer)
                : this.requestGeoLocationError.bind(this, undefined !== event)
        const options = siteConfig.geoLocationOptions

        if (navigator.geolocation) {
            if (isMobile()) {
                options.enableHighAccuracy = true
            }

            // @ts-expect-error because legacy code
            getCurrentPositionUntil(10000, success, failure, options)
        }
    }

    requestGeoLocationSuccess(newFilterUrl: string, position: GeolocationPosition) {
        // Genauigkeitsgrad in Metern, weniger = besser
        // const minimumAccuracy = 5
        // Maximale Anzahl an Versuchen, einen höheren
        //	Genauigkeitsgrad zu erhalten
        // const maxAttempts = 5
        let latitude: string
        let longitude: string
        let redirectPage: string

        if (undefined === window.keepModalOpen || !window.keepModalOpen) {
            $(".modal:visible").modal("hide")
        }

        if ("pushState" in history) {
            LadiesMain.changeUrl({}, document.title, location.pathname + location.search)
        }

        //if (SiteInfo.isMobileDevice && position.coords.accuracy > minimumAccuracy && maxAttempts > position.attempt) {
        //    return
        //}

        // @ts-expect-error yep fix types later tyty
        navigator.geolocation.clearWatch(position.watchId)

        $(document).trigger("location.request:geoLocation", { position: position })

        latitude = position.coords.latitude.toFixed(8)
        longitude = position.coords.longitude.toFixed(8)

        if (isMobile()) {
            redirectPage = "redirectToPageSlug" in SiteInfo ? SiteInfo.redirectToPageSlug : "anzeigenmarkt"
            const filterPath = encodeURI(
                (getCategories().length > 0 ? `/${getCategoriesAsString()}` : "") +
                    (Object.keys(getFilters()).length > 0 ? `/${getFiltersAsString()}` : "") +
                    getQueryParametersAsString("page", "umkreis", "adresse"),
            )
            location.href = `/standort/setzen/?lat=${latitude}&lon=${longitude}&redirect=${redirectPage}&filter-url=${encodeURIComponent(newFilterUrl)}&filter-path=${filterPath}`
        } else {
            fetch(
                `${this.requestUri}?${new URLSearchParams({
                    lat: latitude,
                    lon: longitude,
                }).toString()}`,
                {
                    credentials: "include",
                },
            ).then(async (response) => {
                this.applyNewLocationInformation(newFilterUrl, await response.json(), response.status)
            })
        }
    }

    applyNewLocationInformation(_: string, data: any, status: number) {
        let positionError: PositionError

        if (200 !== status) {
            $(document).trigger("location.error:geoLocation", { status: status, data: data })
            return
        }

        $(document).trigger("location.success:geoLocation", { status: status, data: data })

        if ("object" !== typeof data.city) {
            positionError = new PositionError(PositionError.POSITION_UNAVAILABLE)
            this.requestGeoLocationError(false, positionError)
        }

        const newLocation = data.city

        window.location.href = createNewPathForLocationSlug(window.location.href, newLocation.slug)
    }

    clearUrl = (urlComplete: string) => {
        const queryString = urlComplete.split("?")
        let elements = []
        let newUrlComplete = urlComplete
        const newQueryStringArr = []
        let keepUmkreis = false

        if (queryString[1] && queryString[1] !== "") {
            newUrlComplete = queryString[0]
            elements = queryString[1].split("&")

            for (const i in elements) {
                const getElementArr = elements[i].split("=")
                const getKey = getElementArr[0]
                const getVal = getElementArr[1]

                if ("umkreis" === getKey && "undefined" !== typeof SearchConfig) {
                    if (!SearchConfig.options) {
                        SearchConfig.options = {
                            locationType: "",
                            suchTextOhneOrte: "",
                        }
                    }

                    keepUmkreis = "umkreisLoaded" in SearchConfig.options && true === SearchConfig.options.umkreisLoaded
                }

                if (keepUmkreis || ["umkreis", "page", "suchtext"].indexOf(getKey) < 0) {
                    newQueryStringArr.push(`${getKey}=${getVal}`)
                }
            }
        }

        if (newQueryStringArr.length > 0) {
            newUrlComplete = `${newUrlComplete}?${newQueryStringArr.join("&")}`
        }

        return newUrlComplete
    }

    requestGeoLocationError(withTrigger: boolean, positionError: PositionError) {
        const geoCodeModal = $("#coordinates-modal").removeClass(this.removeGeoClasses)

        $(document).trigger("location.error:geoLocation", { error: positionError })

        switch (positionError.code) {
            case PositionError.PERMISSION_DENIED: {
                // Benutzer hat das Verwenden der Geo-Koordinaten unterbunden
                const denyTimeTaken = new Date().getTime() - this.autoDenyTimer

                if (!this.autoDenied && denyTimeTaken < 500) {
                    this.autoDenied = true

                    if (!withTrigger) {
                        return
                    }
                }

                if (this.autoDenied || this.explicitlyDenied || this.retryHash === location.hash) {
                    openGenericModal(
                        "Für diese Funktion benötigst du die Standorterkennung oder den Ortungsdienst deines Gerätes! " +
                            "<br/><br/>Bitte aktiviere diesen in deinen Einstellungen und versuche es noch einmal." +
                            "<br/><br/>Genauere Informationen zur Standorterkennung erhältst du von deinem Gerätehersteller." +
                            '<br/><br/><button type="button" class="btn btn-default col-xs-4 col-xs-offset-4" id="button-reload-site">Neu versuchen</button>',
                        "Problem bei Standorterkennung",
                    )

                    // TODO at some point close button of generic button should always close the modal and provide a callback to react
                    const genericDialogCloseButton = document.getElementById("generic-dialog-close-button")
                    genericDialogCloseButton?.addEventListener("click", () => {
                        const dialog = document.getElementById("generic-dialog")
                        if (dialog) {
                            dialog.style.display = "none"
                            LadiesMain.removeHashtagFromUrl("geoRetry")
                        }
                    })

                    if (undefined === history.replaceState) {
                        location.hash = this.retryHash
                    } else {
                        LadiesMain.changeUrl({}, $(document).find("title").text(), this.retryHash)
                    }
                    return
                }

                this.explicitlyDenied = true
                InfoPanel.displayMessage(
                    "Deine Geo-Koordinaten wurden unterdrückt. Verwende zur Bestimmung\
                      deines Standortes bitte das Suchfeld für Ort oder Region.",
                    InfoPanel.Type.Info,
                    7.5 * 1000, // Millisekunden
                )
                break
            }

            case PositionError.POSITION_UNAVAILABLE:
                // Das Auslesen der Koordinaten konnte nicht erfolgreich
                //	durchgeführt werden
                geoCodeModal.addClass("geo-unavailable").modal("show")
                break

            case PositionError.TIMEOUT:
                // Das GPS hat zu lange zum Zurückliefern der Koordinaten
                //	gebraucht und der Timeout ist abgelaufen
                geoCodeModal.addClass("geo-gps-timeout").modal("show")
                break

            case PositionError.REQUEST_TIMEOUT:
                // Der Benutzer hat keine Auswahl innerhalb des gesetzten
                //	Zeitlimits getroffen
                InfoPanel.displayMessage(
                    "Bitte treffe eine Auswahl, ob du mit der Ermittlung und\
                      Verwendung deines aktuellen Standortes einverstanden bist.",
                    InfoPanel.Type.Info,
                    7.5 * 1000, // Millisekunden
                )
                break

            case PositionError.GEOLOCATION_UNAVAILABLE:
                // Das navigator.geolocation Objekt wird im verwendeten
                //	Browser des Nutzers nicht unterstützt
                console.info("Error:", "Unavailable")
                break

            default:
                // Andere Fehler landen hier
                console.info("Error:", "Unexpected")
                break
        }
    }

    reloadPage(requiresPageReload = false) {
        if (!requiresPageReload) {
            this.requestGeoLocationInfo(this.reloadPage.bind(this, true))
            return
        }

        location.reload()
    }

    stopEventBubbling = (event: Event) => {
        event.stopPropagation()
    }

    clearToggleHandlers() {
        this.toggleElements.off("click", this.regionCallback)
        $(document).off("click", this.regionCallback)
    }

    removeGeoClasses = (_: number, classes: string) => (classes.match(/geo-\S+/g) || []).join(" ")
}

const locationSelector = new LocationSelectorClass()
if (isMobile()) {
    getShowResultsButtonLocationModal()?.addEventListener("click", () => {
        const selectedLocation = locationSelector.autocomplete.selectedLocation
        const selectedAddress = document.querySelector<HTMLSelectElement>('select[name="address"]')?.value || ""
        if (!selectedLocation && selectedAddress === SearchConfig.location.address) {
            $("#fltRegio").modal("hide")
            return
        }
        removeQueryParameter("page")
        removeQueryParameter("umkreis")

        if (selectedAddress && selectedLocation?.locationType !== "overall") {
            setQueryParameter("adresse", selectedAddress)
        } else {
            removeQueryParameter("adresse")
        }
        const location = selectedLocation || SearchConfig.location
        setFilterLocation({
            ...location,
            // yii api delivers slugs without plz-
            // selected location comes from yii api results
            slug: selectedLocation?.plz ? `${location.plz}-${location.slug}` : location.slug,
        })
        window.location.href = createPathWithAllFilters()
    })
}

$("#fltRegio").on("shown.bs.modal", () => {
    document.documentElement.style.overflow = "hidden"
})

$("#fltRegio").on("hidden.bs.modal", () => {
    document.documentElement.style.overflow = "auto"
})

document.getElementById("button-empty-location")?.addEventListener("click", async () => {
    setShowResultsButtonLoading()
    hideCityAddressesMobile()
    await fetchApiWithNewLocation({
        cityId: 0,
        name: "Alle Regionen",
        postalCodes: [],
        slug: "overall",
        locationType: "overall",
        plz: "",
        address: "",
        addressName: "",
    })
    locationSelector.autocomplete.selectedLocation = {
        cityId: 0,
        name: "Alle Regionen",
        postalCodes: [],
        slug: "",
        locationType: "overall",
        plz: "",
        address: "",
        addressName: "",
    }
})
